import { FeedbackIcon, MinimalNavIcon } from "@storytell/bsky-icons/";
import clsx from "clsx";
import {
  type Component,
  For,
  Match,
  Show,
  Suspense,
  Switch,
  createEffect,
  createResource,
  createSignal,
  onCleanup,
} from "solid-js";
import { isServer } from "solid-js/web";
import { Avatar } from "~/components/avatar/Avatar";
import {
  NavigationPaneMode,
  ExtendedPaneMode,
  isPromptSubmitted,
  useAppUI,
  isExtendedPaneVisible,
} from "./AppUIContext";
import { A, useNavigate } from "@solidjs/router";
import { useWire } from "~/wire";
import { TbApps, TbChevronRight, TbFileText, TbSettings } from "solid-icons/tb";
import { StButton } from "@storytell/ui/buttons";
import { NavigationAssetsPane } from "./NavigationAssetsPane";
import { AnimateChildren } from "@storytell/ui/animation";
import { NavigationConceptsPane } from "./NavigationConceptsPane";
import { createListRecentChatsRequest, useCallOptions, useChatClient, withBearerToken } from "@storytell/cp-client";
import { urls } from "~/lib/urls";

// Constants
const MIN_NAV_WIDTH = 300;

/**
 * NavigationPane Component
 *
 * State Machine Logic:
 * - The panel has only TWO modes: UNDOCKED (default) or DOCKED (user explicitly docked)
 * - When UNDOCKED: Panel is hidden by default, shows on hover with floating appearance
 * - When DOCKED: Panel is always visible with integrated sidebar appearance
 * - No COLLAPSED state - panel conceptually remains UNDOCKED when hidden
 *
 * Animation Expectations:
 * - Panel always slides in/out from the same position (left-0 top-0)
 * - Uses transform for smooth animations, never changes CSS positioning
 * - Maintains visual state (floating vs integrated) throughout animations
 * - No visual jumps during state transitions
 */
export const NavigationPane: Component = () => {
  const wire = useWire();
  const { state, actions } = useAppUI();
  const [isHovering, setIsHovering] = createSignal(false);
  const [isResizing, setIsResizing] = createSignal(false);
  const [isMobile, setIsMobile] = createSignal(false);
  const navigate = useNavigate();

  let paneRef: HTMLDivElement | undefined;
  let resizeHandleRef: HTMLDivElement | undefined;

  const [recentChats] = createResource(
    () => wire.auth.isReady() && wire.auth.token(),
    async (token) => {
      const client = useChatClient();
      const chats = await client.listRecentChats(
        createListRecentChatsRequest({}),
        useCallOptions(withBearerToken(token)),
      );

      // TODO: @andi streaming - handle error gracefully
      if (chats.isErr()) {
        return [];
      }

      return chats.value.chats;
    },
  );

  // Detect mobile device and screen size
  createEffect(() => {
    if (isServer) return;

    const checkMobile = () => {
      const isMobileScreen = window.innerWidth < 768; // Tailwind md breakpoint
      const isTouchDevice = "ontouchstart" in window || navigator.maxTouchPoints > 0;
      setIsMobile(isMobileScreen || isTouchDevice);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    onCleanup(() => {
      window.removeEventListener("resize", checkMobile);
    });
  });

  const onNewChat = () => {
    // Reset all chat and UI state to initial state
    actions.resetChat();
    actions.clearExtendedPaneArtifact();
    actions.setExtendedPaneMode(ExtendedPaneMode.HIDDEN);
    navigate("/");
  };

  // Global mouse position tracking for hit area detection
  createEffect(() => {
    if (isServer || isMobile()) return;

    const handleMouseMove = (e: MouseEvent) => {
      const isInHitArea = e.clientX <= 110; // Within 110px of left edge
      const navWidth = paneWidth();
      const isOverNav = isPanelVisible() && e.clientX <= navWidth; // Mouse is over the visible nav

      // Only respond to mouse if not manually docked and not currently resizing
      if (!isDocked() && !isResizing()) {
        if (isInHitArea && !isHovering()) {
          setIsHovering(true);
          // Ensure undocked width matches minimum docked width
          if (navWidth < MIN_NAV_WIDTH) {
            actions.setNavigationPaneWidth(MIN_NAV_WIDTH);
          }
        } else if (!isInHitArea && !isOverNav && isHovering()) {
          // Only collapse if mouse is outside both hit area AND nav area
          setIsHovering(false);
        }
      }
    };

    document.addEventListener("mousemove", handleMouseMove);

    onCleanup(() => {
      document.removeEventListener("mousemove", handleMouseMove);
    });
  });

  // Auto-show logic: Only responds to hover when not manually docked
  createEffect(() => {
    // Skip auto-behavior if user has explicitly docked the panel
    if (state.navigationPane.mode === NavigationPaneMode.DOCKED) return;

    // Ensure we're in undocked mode when hovering (should already be, but ensures consistency)
    // When not hovering, we stay UNDOCKED - panel just becomes hidden via transform
    if (isHovering()) {
      actions.setNavigationPaneMode(NavigationPaneMode.UNDOCKED);
    }
    // Intentionally no else clause - panel remains UNDOCKED when hidden
  });

  // Resize functionality
  const handleResizeStart = (e: MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);

    const startX = e.clientX;
    const startWidth = state.navigationPane.width;

    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault();
      const deltaX = e.clientX - startX;
      const newWidth = Math.max(MIN_NAV_WIDTH, startWidth + deltaX); // Enforce minimum width

      // Apply width change immediately without batching for responsive feel
      requestAnimationFrame(() => {
        actions.setNavigationPaneWidth(newWidth);
      });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener("mousemove", handleMouseMove, { capture: true });
      document.removeEventListener("mouseup", handleMouseUp, { capture: true });
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      document.body.style.pointerEvents = "";
    };

    document.addEventListener("mousemove", handleMouseMove, { capture: true });
    document.addEventListener("mouseup", handleMouseUp, { capture: true });
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.body.style.pointerEvents = "none"; // Prevent interference from other elements
  };

  // Toggle handler for manual expand/collapse
  const handleToggle = () => {
    actions.toggleNavigationPane();
  };

  // State machine helpers
  const isDocked = () => state.navigationPane.mode === NavigationPaneMode.DOCKED;
  const isUndocked = () => state.navigationPane.mode === NavigationPaneMode.UNDOCKED;
  const paneWidth = () => state.navigationPane.width;

  // Visibility logic: Panel shows when docked OR when undocked + hovering
  const isPanelVisible = () => isDocked() || (isUndocked() && isHovering());

  // Appearance logic: Show floating appearance unless explicitly docked
  const shouldShowUndockedAppearance = () => !isDocked();

  return (
    <>
      {/* Toggle and logo when nav is collapsed */}
      <Show when={!isPanelVisible()}>
        <div class="fixed left-3 top-4 z-50 flex items-center gap-3">
          {/* Toggle Button */}
          <button
            type="button"
            class="p-2 hover:bg-canvas-hover transition-colors focus:outline-none rounded-md"
            onClick={handleToggle}
            aria-label="Expand navigation"
          >
            <MinimalNavIcon class="w-6 h-6 text-on-background-variant dark:text-dark-on-background-variant" />
          </button>

          {/* Storytell Logo - hide when extended pane is open and nav is undocked */}
          <Show when={wire.chat.shouldShowContent() && !(isExtendedPaneVisible(state.extendedPane) && !isDocked())}>
            <img
              src="/images/storytell-logo-full-light.svg"
              alt="Storytell"
              class="h-6 dark:hidden transition-opacity duration-1000 opacity-100"
            />
            <img
              src="/images/storytell-logo-full-dark.svg"
              alt="Storytell"
              class="h-6 hidden dark:block transition-opacity duration-1000 opacity-100"
            />
          </Show>
        </div>
      </Show>

      {/* Navigation pane */}
      <nav
        ref={paneRef}
        class={clsx(
          // Base structure - never changes during animations to prevent jumps
          "fixed z-50 flex flex-col",

          // Background styling - always semi-transparent with blur to show background images
          "bg-canvas/80 backdrop-blur-md",

          // Stable positioning - always anchored at top-left, never changes
          "left-0 top-0",

          // Dimensions - use margin for floating effect instead of position changes
          shouldShowUndockedAppearance()
            ? "h-[calc(100vh-16px)] m-2" // Floating: margin creates 8px spacing on all sides
            : "h-full", // Integrated: full height, no margin

          // Animation - only transform changes, preserving layout stability
          "transition-transform duration-300 ease-out",
          isPanelVisible()
            ? "translate-x-0" // Visible: no transform offset
            : "-translate-x-[calc(100%+8px)]", // Hidden: slide completely off-screen + 8px buffer

          // Visual styling - appearance reflects conceptual state, not visibility
          isDocked()
            ? [
                // Docked: integrated sidebar appearance (flush with viewport edge)
                "border-r border-primary-border",
              ]
            : [
                // Undocked: floating panel appearance (elevated with shadows)
                "shadow-[0_8px_32px_rgba(0,0,0,0.12)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.3)] rounded-xl",
                "border border-primary-border",
                "overflow-hidden",
              ],
        )}
        style={{ width: `${paneWidth()}px` }}
        aria-label="Navigation pane"
      >
        {/* Header - fixed at top */}
        <header class="flex-shrink-0 px-4 py-3">
          <div class="flex items-center justify-between">
            {/* Collapse button - same as collapsed state */}
            <div class="flex items-center gap-3">
              <button
                type="button"
                class="p-2 rounded-md hover:bg-canvas-hover transition-colors focus:outline-none"
                onClick={handleToggle}
                aria-label="Collapse navigation"
              >
                <MinimalNavIcon class="w-6 h-6 text-muted-text" />
              </button>

              {/* Storytell Logo in expanded state - hide when extended pane is open and nav is undocked */}
              <Show when={isPromptSubmitted(state.chat) && !(isExtendedPaneVisible(state.extendedPane) && !isDocked())}>
                <img
                  src="/images/storytell-logo-full-light.svg"
                  alt="Storytell"
                  class="h-6 dark:hidden transition-opacity duration-1000 opacity-100"
                />
                <img
                  src="/images/storytell-logo-full-dark.svg"
                  alt="Storytell"
                  class="h-6 hidden dark:block transition-opacity duration-1000 opacity-100"
                />
              </Show>
            </div>
          </div>
        </header>

        {/* Scrollable content area */}
        <div class="flex-1 overflow-y-auto flex flex-col">
          {/* Action Bar */}
          <div class="flex-shrink-0 px-4 py-3">
            <button
              type="button"
              onClick={onNewChat}
              class="w-full flex items-center justify-center gap-2 px-4 py-2 bg-brand text-on-brand hover:bg-brand-hover border border-primary-border rounded-lg transition-colors text-sm font-medium"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
              </svg>
              New chat
              <span class="ml-auto text-xs text-on-brand/60 font-mono">⌘ K</span>
            </button>
          </div>

          {/* Chat List */}
          <div class="px-4 py-3 space-y-2">
            <StButton
              onClick={() => {
                if (state.navigationPane.secondaryPane !== "assets") {
                  actions.setNavigationPaneMode(NavigationPaneMode.DOCKED);
                  actions.openSecondaryPane("assets");
                } else {
                  actions.closeSecondaryPane();
                }
              }}
              icon={TbFileText}
              data-active={state.navigationPane.secondaryPane === "assets"}
              class="w-full text-base justify-start border border-transparent px-4 data-[active=true]:bg-asset-bg data-[active=true]:text-asset-text data-[active=true]:border-asset-border focus:ring-asset-border"
              iconClass="size-4"
              noBorder
            >
              <span class="flex-auto text-left">Assets</span>
              <TbChevronRight class="size-4" />
            </StButton>

            <StButton
              onClick={() => {
                if (state.navigationPane.secondaryPane !== "concepts") {
                  actions.setNavigationPaneMode(NavigationPaneMode.DOCKED);
                  actions.openSecondaryPane("concepts");
                } else {
                  actions.closeSecondaryPane();
                }
              }}
              icon={TbApps}
              data-active={state.navigationPane.secondaryPane === "concepts"}
              class="w-full text-base justify-start border border-transparent px-4 data-[active=true]:bg-concept-bg data-[active=true]:text-concept-text data-[active=true]:border-concept-border focus:ring-concept-border"
              iconClass="size-4"
              noBorder
            >
              <span class="flex-auto text-left">Concepts</span>
              <TbChevronRight class="size-4" />
            </StButton>
          </div>

          <Suspense>
            <div class="flex flex-col flex-auto overflow-y-auto px-4 py-2 space-y-2">
              <span class="text-xs text-secondary-text">Recent Chats:</span>

              <For each={recentChats()}>
                {(item) => (
                  <A
                    activeClass="bg-canvas-hover"
                    class="py-1 px-2 rounded-md hover:bg-panel-hover w-full max-w-full -ml-2"
                    href={urls.chatPage(item.chatId)}
                  >
                    {item.title}
                  </A>
                )}
              </For>
            </div>
          </Suspense>
        </div>

        {/* Fixed Footer Sections */}
        <div class="flex-shrink-0">
          {/* Share Section */}
          <div class="px-4 py-3 border-t border-background-decoration dark:border-dark-background-decoration">
            <div class="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-surface-hover dark:hover:bg-dark-surface-hover transition-colors cursor-pointer">
              <div class="flex-shrink-0">
                <svg
                  class="w-5 h-5 text-on-background-variant dark:text-dark-on-background-variant"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"
                  />
                </svg>
              </div>
              <div class="flex-1 min-w-0">
                <div class="text-sm text-on-background dark:text-dark-on-background">Share Storytell with a friend</div>
                <div class="text-xs text-on-background-variant dark:text-dark-on-background-variant">
                  Get 500 credits each
                </div>
              </div>
              <div class="flex-shrink-0">
                <svg
                  class="w-4 h-4 text-on-background-variant dark:text-dark-on-background-variant"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </div>

          {/* Feedback Section */}
          <div class="px-4 py-3 border-t border-background-decoration dark:border-dark-background-decoration">
            <div class="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer">
              <FeedbackIcon class="w-5 h-5 text-gray-600 dark:text-gray-400" />
              <div>
                <div class="text-sm font-medium text-gray-900 dark:text-gray-100">Help us improve Storytell</div>
                <div class="text-xs text-gray-500 dark:text-gray-400">Share your feedback and suggestions</div>
              </div>
            </div>
          </div>

          {/* User Profile Footer */}
          <div class="px-4 py-3 border-t border-background-decoration dark:border-slate-700 bg-background/60 dark:bg-[#1F2538]/60">
            <div class="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-surface-hover dark:hover:bg-dark-surface-hover transition-colors cursor-pointer">
              <div class="flex-shrink-0">
                <Avatar src={wire.auth.user()?.avatarUrl} name={wire.auth.user()?.displayName} size="md" />
              </div>
              <div class="flex-1 min-w-0">
                <div class="text-sm font-medium text-on-background dark:text-dark-on-background">
                  {wire.auth.user()?.displayName || "You"}
                </div>
              </div>
              <div class="flex-shrink-0 flex gap-1">
                <button type="button" class="p-1 rounded hover:bg-panel-hover transition-colors">
                  <TbSettings class="size-5" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Resize handle */}
        <Show when={isPanelVisible()}>
          <div
            ref={resizeHandleRef}
            class="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-primary/50 dark:hover:bg-dark-primary/50 transition-colors"
            onMouseDown={handleResizeStart}
            aria-label="Resize navigation pane"
          />
        </Show>

        {/* Extra pane */}
      </nav>
      <AnimateChildren
        enter={{
          right: ["100%", "10%"],
          opacity: [0, 1],
        }}
        exit={{
          right: ["10%", "100%"],
          opacity: [1, 0],
        }}
        show={state.navigationPane.secondaryPane !== null}
      >
        {(anim) => (
          <div
            ref={anim.ref}
            class="fixed z-50 inset-y-0 bg-panel border-r max-w-5xl border-primary-border shadow-[4px_0_16px_-4px_rgba(0,0,0,0.15)]"
            style={{ left: `${paneWidth()}px` }}
          >
            <Switch>
              <Match when={state.navigationPane.secondaryPane === "assets"}>
                <NavigationAssetsPane />
              </Match>
              <Match when={state.navigationPane.secondaryPane === "concepts"}>
                <NavigationConceptsPane />
              </Match>
            </Switch>
          </div>
        )}
      </AnimateChildren>
    </>
  );
};
